const CHECKOUT_ERROR_CODES = [
  'tokenization_failed',
  'fraud_declined',
  'fraud_check_failed',
  'invalid_payment_data',
  'invalid_payment_source',
  'payment_source_not_owned',
  'payment_customer_creation_failed',
  'payment_card_creation_failed',
  'issuer_installment_failed',
  'instrument_verification_failed',
  'card_verification_required',
  'invalid_card_data',
  'card_source_already_used',
  'card_source_expired',
  'seller_card_save_failed',
  'card_migration_failed',
  'payment_authorization_failed',
  'order_validation_failed',
  'recaptcha_failed',
  'buyer_verification_failed',
  'network_failure',
  'client_error',
  'unknown_error',
  'internal_error',
] as const;

export type CheckoutErrorCode = (typeof CHECKOUT_ERROR_CODES)[number];

export interface NormalizedCheckoutError {
  errorCode: CheckoutErrorCode;
  errorDetails?: string;
}

export interface CheckoutApiError {
  error_code?: string;
  http_status?: number;
  data?: {
    error_code?: string;
  };
}

const MAX_DETAIL_VALUE_LENGTH = 40;

// Bounds a value echoed into errorDetails. A structured value logs only its
// type, since its contents are unbounded.
const boundedDetailValue = (value: unknown, fallback = 'missing'): string => {
  if (value === null || value === undefined) {
    return fallback;
  }
  const text = typeof value === 'object' ? 'object' : String(value);
  return text.slice(0, MAX_DETAIL_VALUE_LENGTH);
};

// verifyBuyer rejects with SDK details of { type, code, message }. Only type
// and code are logged; the message is free text.
const verifyBuyerErrorDetails = (error: Error): string => {
  const { errors } = error as { errors?: unknown };
  const { type, code } = (Array.isArray(errors) && errors[0]) || {};
  const sdkCode = code ? `, sdk_code: ${boundedDetailValue(code)}` : '';
  return `sdk_error: ${boundedDetailValue(type)}${sdkCode}`;
};

const isCheckoutErrorCode = (value: unknown): value is CheckoutErrorCode =>
  typeof value === 'string' &&
  CHECKOUT_ERROR_CODES.includes(value as CheckoutErrorCode);

const errorCodeFromApiError = (
  error: unknown,
): CheckoutErrorCode | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const apiError = error as CheckoutApiError;
  const errorCode = apiError.error_code || apiError.data?.error_code;
  return isCheckoutErrorCode(errorCode) ? errorCode : undefined;
};

const httpStatusFromApiError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { http_status: httpStatus } = error as CheckoutApiError;
  return typeof httpStatus === 'number' ? httpStatus : undefined;
};

const normalizeCheckoutError = ({
  error,
  httpStatus,
  httpErrorCode,
}: {
  error?: unknown;
  httpStatus?: number;
  // Stage-specific code for an HTTP error response without an allowlisted
  // error_code. Never applied to exceptions, so a thrown error can't inherit it.
  httpErrorCode?: CheckoutErrorCode;
}): NormalizedCheckoutError => {
  const resolvedHttpStatus = httpStatus || httpStatusFromApiError(error);
  const apiErrorCode = errorCodeFromApiError(error);
  if (apiErrorCode) {
    return {
      errorCode: apiErrorCode,
      ...(resolvedHttpStatus
        ? { errorDetails: `http_status: ${resolvedHttpStatus}` }
        : {}),
    };
  }

  if (resolvedHttpStatus) {
    const statusErrorCode =
      resolvedHttpStatus >= 500 ? 'internal_error' : 'unknown_error';
    return {
      errorCode: httpErrorCode || statusErrorCode,
      errorDetails: `http_status: ${resolvedHttpStatus}`,
    };
  }

  if (error instanceof Error && error.name === 'VerifyBuyerError') {
    return {
      errorCode: 'buyer_verification_failed',
      errorDetails: verifyBuyerErrorDetails(error),
    };
  }

  // Network failures are tagged with error_code where the fetch rejects, so
  // anything else without a response was thrown in the browser.
  return { errorCode: 'client_error' };
};

// Callers keep checkout spinning until this resolves, so a body that stalls
// after its headers must not block the buyer from retrying.
const RESPONSE_BODY_TIMEOUT_MS = 2000;

const normalizeCheckoutResponseError = async ({
  response,
  httpErrorCode,
}: {
  response: Response;
  httpErrorCode?: CheckoutErrorCode;
}): Promise<NormalizedCheckoutError> => {
  let error: unknown;
  let bodyTimedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const bodyTimeout = new Promise<void>((resolve) => {
    timeoutId = setTimeout(() => {
      bodyTimedOut = true;
      resolve();
    }, RESPONSE_BODY_TIMEOUT_MS);
  });
  try {
    error = await Promise.race([response.clone().json(), bodyTimeout]);
  } catch {
    // The RUM detail is deliberately limited to status; raw response text can contain sensitive data.
  } finally {
    clearTimeout(timeoutId);
  }

  const normalizedError = normalizeCheckoutError({
    error,
    httpStatus: response.status,
    httpErrorCode,
  });
  return bodyTimedOut
    ? {
        ...normalizedError,
        errorDetails: `${normalizedError.errorDetails}, body_timeout`,
      }
    : normalizedError;
};

/**
 * Normalizes an order completion (`/complete`) error for RUM. Unlike other
 * checkout endpoints, its messages are a fixed set of server strings, and
 * several share one code (every validation failure is order_validation_failed),
 * so the message is kept next to the HTTP status.
 * @param error - error thrown by the completeOrder client
 * @returns normalized error code and details
 */
const normalizeCompleteOrderError = (
  error?: Error | null,
): NormalizedCheckoutError => {
  const normalizedError = normalizeCheckoutError({ error });
  const errorDetails = [
    normalizedError.errorDetails,
    error?.message && `message: ${error.message}`,
  ]
    .filter(Boolean)
    .join(', ');

  return errorDetails ? { ...normalizedError, errorDetails } : normalizedError;
};

export {
  RESPONSE_BODY_TIMEOUT_MS,
  boundedDetailValue,
  normalizeCheckoutError,
  normalizeCheckoutResponseError,
  normalizeCompleteOrderError,
};
